type DefinitionFeedbackObject = {
    warnings: string[];
    errors: string[];
};

export default class DefinitionParser {
    static instance = new DefinitionParser();
    static getInstance() {
        if (!DefinitionParser.instance) {
            DefinitionParser.instance = new DefinitionParser();
        }
        return DefinitionParser.instance;
    }

    private constructor() {
        // Private constructor to prevent instantiation
    }

    parseDefinition(definition: string) {
        const parsedDefinition = JSON.parse(definition);
        return parsedDefinition;
    }

    stringifyDefinition(definition: any) {
        return JSON.stringify(definition);
    }

    private validateDefinition(definition: Definition): {
        isValid: boolean;
        warnings: string[];
        errors: string[];
        hasIdErrors: boolean;
    } {
        if (!definition || typeof definition !== 'object') {
            return {
                isValid: false,
                errors: ['Definition is not an object'],
                warnings: [],
                hasIdErrors: false,
            };
        }

        const { screens, sharing } = definition;

        if (!Array.isArray(screens)) {
            return {
                isValid: false,
                errors: ['Screens is not an array'],
                warnings: [],
                hasIdErrors: false,
            };
        }

        // Validate that all screens are valid objects
        for (let i = 0; i < screens.length; i++) {
            const screen = screens[i];
            if (!screen || typeof screen !== 'object') {
                return {
                    isValid: false,
                    errors: [`Screen at index ${i} is not a valid object`],
                    warnings: [],
                    hasIdErrors: false,
                };
            }
            if (!screen.id) {
                return {
                    isValid: false,
                    errors: [`Screen at index ${i} is missing an id`],
                    warnings: [],
                    hasIdErrors: false,
                };
            }
        }

        if (sharing && !Array.isArray(sharing)) {
            return {
                isValid: false,
                errors: ['Sharing is not an array'],
                warnings: [],
                hasIdErrors: false,
            };
        }

        if (sharing) {
            for (const sharingScreen of sharing) {
                if (!sharingScreen.name) {
                    return {
                        isValid: false,
                        errors: ['Sharing screen name is required'],
                        warnings: [],
                        hasIdErrors: false,
                    };
                }
                if (!Array.isArray(sharingScreen.screens)) {
                    return {
                        isValid: false,
                        errors: ['Sharing screens is not an array'],
                        warnings: [],
                        hasIdErrors: false,
                    };
                }
            }
        }

        const {
            warnings: processingFlowWarnings,
            errors: processingFlowErrors,
        } = this.detectProcessingFlows(definition);

        // Generate Variable Map
        const {
            variableMap,
            errors: variableMapErrors,
            warnings: variableMapWarnings,
        } = this.generateVariableMap(definition);

        const {
            dynamicTextMap,
            errors: dynamicTextMapErrors,
            warnings: dynamicTextMapWarnings,
        } = this.generateDynamicTextMap(definition);

        const { errors: navigationErrors, warnings: navigationWarnings } =
            this.validateNavigation(definition);

        const { errors: idClashErrors, warnings: idClashWarnings } =
            this.detectIdClash(definition);

        const {
            errors: navigationToWelcomeScreenErrors,
            warnings: navigationToWelcomeScreenWarnings,
        } = this.detectNavigationToWelcomeScreen(definition);

        // Detect unresolved variables
        // Detect variables used before they are defined
        // Detect unused variables
        // Detect all variables created in the definition
        // Detect all navigation paths
        // Detect if a reset action is present
        // Detect if a capture step is present
        // Detects if a processing flow is present
        // Detect if a processing flow is not used
        // Detect if ids clash.

        console.log('Variable Map:', variableMap);
        console.log('Dynamic Text Map:', dynamicTextMap);

        return {
            isValid: true,
            warnings: [
                ...processingFlowWarnings,
                ...variableMapWarnings,
                ...dynamicTextMapWarnings,
                ...navigationWarnings,
                ...idClashWarnings,
                ...navigationToWelcomeScreenWarnings,
            ],
            errors: [
                ...processingFlowErrors,
                ...variableMapErrors,
                ...dynamicTextMapErrors,
                ...navigationErrors,
                ...idClashErrors,
                ...navigationToWelcomeScreenErrors,
            ],
            hasIdErrors: idClashErrors.length > 0,
        };
    }

    private detectNavigationToWelcomeScreen(definition: Definition) {
        const { screens } = definition;
        let errors: string[] = [];
        let warnings: string[] = [];

        const firstScreen = screens[0];

        if (!firstScreen) {
            return {
                errors: [],
                warnings: [],
            };
        }

        const firstScreenId = firstScreen.id;

        screens.forEach((screen) => {
            const { navigateDestination } = screen;

            if (navigateDestination === firstScreenId) {
                warnings.push(
                    `Screen ${screen.id} navigates to the first screen without resetting state.`,
                );
            }

            // Ensure elements array exists, default to empty array if undefined
            const elements = screen.elements || [];
            elements.forEach((element) => {
                if (element.type === 'button') {
                    const metadata =
                        element.metadata as unknown as UIButtonMetadata;

                    // @ts-ignore
                    if (!metadata) {
                        return;
                    }

                    // @ts-ignore
                    if (!metadata.action) {
                        return;
                    }

                    // @ts-ignore
                    let isAResettingAction =
                        metadata.action === 'reset' ||
                        metadata.action === 'shareAndReset' ||
                        metadata.action.includes('reset') ||
                        metadata.action.includes('shareAndReset');

                    // @ts-ignore
                    if (
                        metadata?.destination === firstScreenId &&
                        !isAResettingAction
                    ) {
                        warnings.push(
                            `Button ${element.id} navigates to the first screen without resetting state.`,
                        );
                    }
                }
            });
        });

        return { errors, warnings };
    }

    private validateNavigation(definition: Definition) {
        const { screens } = definition;
        let errors: string[] = [];
        let warnings: string[] = [];

        let screenIds = screens.map((screen) => {
            return screen.id;
        });

        screens.forEach((screen) => {
            const { stage } = screen;

            if (stage.metadata) {
                let metadata = stage.metadata as CaptureMetadata;
                if (metadata?.postCaptureDestination) {
                    if (!screenIds.includes(metadata.postCaptureDestination)) {
                        errors.push(
                            `Invalid screen reference: ${metadata.postCaptureDestination}, check postCaptureDestination for screen ${screen.id}.`,
                        );
                    }
                }
            }
        });

        return { errors, warnings };
    }

    private detectIdClash(definition: Definition) {
        let errors: string[] = [];
        let warnings: string[] = [];

        try {
            const { screens, sharing } = definition;

            // Early return if screens is not valid
            if (!screens || !Array.isArray(screens)) {
                errors.push('Screens array is invalid or missing');
                return { errors, warnings };
            }

            let screenIds = screens.map((screen) => {
                // Ensure screen exists and has required properties
                if (!screen || typeof screen !== 'object') {
                    return '';
                }

                // Ensure elements array exists, default to empty array if undefined
                const elements = screen.elements || [];
                let elementIds = elements.map((element) => {
                    return element.id;
                });

                elementIds.forEach((elementId) => {
                    if (
                        elementIds.filter((id) => id === elementId).length > 1
                    ) {
                        errors.push(`Element id ${elementId} is not unique.`);
                    }
                });
                return screen.id || '';
            });

            let sharingScreenIds = sharing?.flatMap((sharing) => {
                // Ensure sharing.screens exists and is an array
                if (!sharing.screens || !Array.isArray(sharing.screens)) {
                    return [];
                }

                return sharing.screens.map((screen: any) => {
                    // Ensure screen exists and has required properties
                    if (!screen || typeof screen !== 'object') {
                        return '';
                    }

                    // Ensure elements array exists, default to empty array if undefined
                    const elements = screen.elements || [];
                    let elementIds = elements.map((element: any) => {
                        return element.id;
                    });

                    elementIds.forEach((elementId: string) => {
                        if (
                            elementIds.filter((id: string) => id === elementId)
                                .length > 1
                        ) {
                            errors.push(
                                `Element id ${elementId} is not unique.`,
                            );
                        }
                    });

                    return screen.id || '';
                });
            });

            let allScreenIds = [...screenIds, ...(sharingScreenIds ?? [])];

            allScreenIds.forEach((screenId) => {
                if (allScreenIds.filter((id) => id === screenId).length > 1) {
                    errors.push(`Screen id ${screenId} is not unique.`);
                }
            });

            return { errors, warnings };
        } catch (error) {
            console.error('Error in detectIdClash:', error);
            errors.push(
                `Error detecting ID clashes: ${
                    error instanceof Error ? error.message : 'Unknown error'
                }`,
            );
            return { errors, warnings };
        }
    }

    public generateScreenGraph(screens: ScreenDefinition[]) {
        const screenGraph: Record<string, string[]> = {};

        screens.forEach((screen) => {
            const { id, stage, navigateDestination } = screen;
            const metadata = stage?.metadata ?? {};

            // Add the screen to the graph
            screenGraph[id] = [];

            // Add the screen's postCaptureDestination to the graph
            // @ts-ignore
            if (metadata?.postCaptureDestination) {
                // @ts-ignore
                screenGraph[id].push(metadata.postCaptureDestination);
            }

            // @ts-ignore
            if (metadata?.postProcessingDestination) {
                // @ts-ignore
                screenGraph[id].push(metadata.postProcessingDestination);
            }

            if (navigateDestination) {
                screenGraph[id].push(navigateDestination);
            }

            // For each element in the screen, see if it has a button, see if it has a destination
            // If it does, add it to the graph
            // Ensure elements array exists, default to empty array if undefined
            const elements = screen.elements || [];
            elements.forEach((element) => {
                if (element.type === 'button') {
                    const { metadata } = element;
                    // @ts-ignore
                    if (metadata?.destination) {
                        // @ts-ignore
                        screenGraph[id].push(metadata.destination);
                    }
                }
            });
        });

        console.log('Screen Graph:', screenGraph);
        let edges: { id: string; source: string; target: string }[] = [];
        for (const screen in screenGraph) {
            screenGraph[screen].forEach((target) => {
                edges.push({
                    id: `e${screen}-${target}`,
                    source: screen,
                    target: target,
                });
            });
        }

        console.log('Edges:', edges);

        return { screenGraph, edges };
    }

    private generateVariableMap(definition: Definition) {
        const { screens } = definition;
        const variableMap: Record<string, string> = {};
        let errors: string[] = [];
        let warnings: string[] = [];

        screens.forEach((screen) => {
            // Ensure elements array exists, default to empty array if undefined
            const elements = screen.elements || [];
            elements.forEach((element) => {
                if (element.type === 'input') {
                    const { metadata, id } = element;

                    if (!metadata.key) {
                        warnings.push(
                            `Input element with id ${id} does not have a key`,
                        );
                        return;
                    }

                    variableMap[id] = metadata.key ?? '';
                }
            });

            if (screen.stage.type === 'capture') {
                const metadata = screen.stage.metadata as CaptureMetadata;

                if (!metadata) {
                    errors.push(
                        `Capture stage with id ${screen.id} does not have a flowId`,
                    );
                }

                if (metadata.mode === 'clicker') {
                    if (!metadata.shouldNavigateAfterCapture) {
                        variableMap[
                            `clicker-${screen.id}`
                        ] = `clicker-${screen.id}`;
                    }
                }

                if (metadata.mode === 'gif') {
                    variableMap[`gif-${screen.id}-0`] = `gif-${screen.id}-0`;
                    variableMap[`gif-${screen.id}-1`] = `gif-${screen.id}-1`;
                    variableMap[`gif-${screen.id}-2`] = `gif-${screen.id}-2`;
                    variableMap[`gif-${screen.id}-3`] = `gif-${screen.id}-3`;
                    variableMap[`gif-${screen.id}-4`] = `gif-${screen.id}-4`;
                    variableMap[`gif-${screen.id}-5`] = `gif-${screen.id}-5`;
                    variableMap[`gif-${screen.id}-6`] = `gif-${screen.id}-6`;
                    variableMap[`gif-${screen.id}-7`] = `gif-${screen.id}-7`;
                }

                if (metadata.mode === 'sequence' && metadata.photoCount) {
                    for (let i = 0; i < metadata.photoCount; i++) {
                        variableMap[
                            `sequence-${screen.id}-${i}`
                        ] = `sequence-${screen.id}-${i}`;
                    }
                }
            }
        });

        return { variableMap, errors, warnings };
    }

    private generateDynamicTextMap(definition: Definition) {
        const { screens } = definition;
        const dynamicTextMap: Record<string, string> = {};
        let errors: string[] = [];
        let warnings: string[] = [];

        screens.forEach((screen) => {
            // Ensure elements array exists, default to empty array if undefined
            const elements = screen.elements || [];
            elements.forEach((element) => {
                if (element.type === 'dynamicText') {
                    const { metadata, id } = element;
                    // treat metadata as UIDynamicTextMetadata
                    const dynamicTextMetadata =
                        metadata as UIDynamicTextMetadata;

                    if (!dynamicTextMetadata.identifier) {
                        errors.push(
                            `DynamicText element with id ${id} does not have an identifier`,
                        );
                        return;
                    }

                    dynamicTextMap[`${screen.id}-${id}`] =
                        dynamicTextMetadata.identifier ?? '';
                }
            });
        });

        return { dynamicTextMap, errors, warnings };
    }

    private detectProcessingFlows(
        definition: Definition,
    ): DefinitionFeedbackObject {
        const { processingFlows } = definition;
        const processingFlowIds = Object.keys(processingFlows);

        const warnings: string[] = [];
        const errors: string[] = [];

        if (processingFlowIds.length === 0) {
            warnings.push('No processing flows present');
        }

        const captureStages = this.getCaptureStages(definition);
        const processingStages = this.getProcessingStages(definition);

        let captureStageProcessingFlowIds = captureStages
            .map((stage) => {
                return stage.flowId;
            })
            .filter((flowId) => flowId != null); // Filter out undefined/null flowIds

        let processingStageProcessingFlowIds = processingStages
            .map((stage) => {
                return stage.flowId;
            })
            .filter((flowId) => flowId != null); // Filter out undefined/null flowIds

        captureStageProcessingFlowIds.forEach((flowId) => {
            if (!processingFlowIds.includes(flowId.toString())) {
                errors.push(
                    `Capture stage with flowId ${flowId} does not have a processing flow`,
                );
            }
        });

        processingStageProcessingFlowIds.forEach((flowId) => {
            if (!processingFlowIds.includes(flowId.toString())) {
                errors.push(
                    `Processing stage with flowId ${flowId} does not have a processing flow`,
                );
            }
        });

        return { warnings, errors };
    }

    private getCaptureStages(definition: Definition): CaptureMetadata[] {
        const { screens } = definition;
        const captureStages = screens.filter((screen) => {
            return screen.stage.type === 'capture' && screen.stage.metadata;
        });

        const captureMetadata = captureStages
            .map((screen) => {
                return screen.stage.metadata as CaptureMetadata;
            })
            .filter((metadata) => metadata != null);

        return captureMetadata;
    }

    private getProcessingStages(definition: Definition) {
        const { screens } = definition;
        const processingStages = screens.filter((screen) => {
            return screen.stage.type === 'processing' && screen.stage.metadata;
        });

        const processingMetadata = processingStages
            .map((screen) => {
                return screen.stage.metadata as ProcessingMetadata;
            })
            .filter((metadata) => metadata != null);

        return processingMetadata;
    }

    analyzeDefinition(definition: Definition) {
        const { screens, processingFlows, definitionVersion } = definition;

        const { warnings, errors, isValid, hasIdErrors } =
            this.validateDefinition(definition);

        return {
            screenCount: screens.length,
            processingFlowCount: Object.keys(processingFlows).length,
            definitionVersion,
            warnings,
            isValid,
            errors,
            hasIdErrors,
        };
    }
}
